import { formatMoney } from "@tovi/shared";
import type { SyncState } from "@tovi/pos-sync";
import { Badge, Button, Icon, useTranslate } from "@tovi/ui";
import { releaseLabel } from "@/lib/bills.ts";
import { usePress } from "@/lib/use-press.ts";
import { optionsOf, unitCost, type Cart } from "@/lib/cart.ts";
import { can } from "@/lib/staff.ts";

/**
 * The running cart (`08-pos-terminal-and-qr.md` § A).
 *
 * "The cart is the permanent right-hand column and is never navigated away
 * from. Table/customer context sits above the cart." So this component owns
 * the right edge of the screen for the whole service, and everything else
 * opens over it.
 *
 * Two things the spec makes explicit and this shows separately: the batch a
 * line belongs to — "Latest Order (N)" against "Ordered (N)" — because a
 * course added later prints as an increment rather than a reprint, and the
 * sync state, because a cashier working offline needs to know it without
 * asking.
 *
 * **Layout derived from the spec text, not a capture.** The order of the
 * summary rows and the placement of the two big buttons are judgement calls.
 * So is putting the whole-bill discount on its own summary row rather than
 * behind a keypad function key: the row is already there to show the amount,
 * and a cashier looking for the discount looks at the total.
 */
export function CartPane({
  cart,
  onSettle,
  onDiscount,
  onAmendLine,
  onBillActions,
  onPickTable,
  onEditCovers,
  onRelease,
  syncState,
}: {
  cart: Cart;
  onSettle: () => void;
  onDiscount: () => void;
  onAmendLine: (itemId: string) => void;
  onBillActions: () => void;
  onPickTable: () => void;
  onEditCovers: () => void;
  onRelease: () => void;
  syncState: SyncState;
}) {
  const t = useTranslate();
  const priced = cart.priced;
  const currency = cart.currency;
  const fired = cart.lines.filter((line) => line.isFired);
  const bill = cart.bill;
  // The same gesture the menu tiles use: a tap picks the table, a hold amends
  // what is on the chip. Only a seated bill has covers — take-away has nobody
  // to count — so on one there is nothing for the hold to do.
  const press = usePress({
    onTap: onPickTable,
    onHold: bill?.table ? onEditCovers : () => {},
  });

  return (
    <aside className="flex w-96 shrink-0 flex-col border-s border-line bg-surface">
      {/* Table and covers, above the cart as § A places them */}
      <header className="border-b border-line p-3">
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            title={bill?.table ? t("pos.cart.editCovers") : undefined}
            {...press}
            className="flex-1 select-none rounded-control border border-line px-3 py-2 text-start"
          >
            <span className="block text-xs text-ink-muted">
              {bill
                ? bill.table
                  ? t("pos.cart.table")
                  : t("pos.cart.takeAway")
                : t("pos.bill.startTitle")}
            </span>
            <span className="block text-sm font-semibold text-ink-body">
              {bill
                ? bill.table
                  ? `${bill.table.name} · ${t("pos.cart.covers", { count: cart.covers })}`
                  : (bill.orderNo ?? t("pos.cart.takeAway"))
                : t("pos.bill.chooseTable")}
            </span>
          </button>
          <SyncBadge state={syncState} />
          {/* Merge and split, which are rare and do not end the service. The
              footer's three buttons are the ones pressed at every table. */}
          {bill ? (
            <button
              type="button"
              aria-label={t("pos.billActions.title")}
              className="rounded-control border border-line px-2 py-2 text-ink-muted"
              onClick={onBillActions}
            >
              <Icon name="more-horizontal" />
            </button>
          ) : null}
        </div>

        {/* What the server does not know yet, said plainly rather than implied
            by a spinner: a bill still queued is a bill no other terminal can
            see, and the cashier is the one who has to know that. */}
        {bill?.status === "opening" ? (
          <p className="mt-2 text-xs text-ink-muted">{t("pos.bill.notSent")}</p>
        ) : null}
        {bill?.status === "failed" ? (
          <p className="mt-2 text-xs text-danger">{bill.failure ?? t("pos.bill.failed")}</p>
        ) : null}
      </header>

      <div className="flex-1 overflow-y-auto">
        {cart.lines.length === 0 ? (
          <div className="flex flex-col items-start gap-3 p-4">
            <p className="text-sm text-ink-muted">{t("pos.cart.empty")}</p>
            {/* Releasing is only ever offered while there is nothing to lose,
                which is exactly the state this branch renders. */}
            {bill ? (
              <Button variant="secondary" onClick={onRelease}>
                {t(releaseLabel(bill.diningStyle))}
              </Button>
            ) : null}
          </div>
        ) : (
          <>
            {fired.length > 0 ? (
              <Section label={t("pos.cart.ordered", { count: fired.length })}>
                {fired.map((line) => (
                  <Line
                    key={line.key}
                    line={line}
                    currency={currency}
                    cart={cart}
                    onAmend={onAmendLine}
                    isFired
                  />
                ))}
              </Section>
            ) : null}
            {cart.unfired.length > 0 ? (
              <Section label={t("pos.cart.latestOrder", { count: cart.unfired.length })}>
                {cart.unfired.map((line) => (
                  <Line key={line.key} line={line} currency={currency} cart={cart} />
                ))}
              </Section>
            ) : null}
          </>
        )}
      </div>

      <footer className="border-t border-line p-3">
        {priced ? (
          <dl className="mb-3 flex flex-col gap-1 text-sm">
            <Row
              label={t("pos.cart.subtotal")}
              value={formatMoney(priced.subtotal.amount.toString(), currency)}
            />
            <button
              type="button"
              className="flex justify-between text-left text-ink-muted underline decoration-dotted underline-offset-4 disabled:no-underline disabled:opacity-60"
              // `orders:discount` is what `POST /orders/{id}/discount`
              // checks — not `items:price`, which is the back office repricing
              // the menu. Disabled rather than hidden: the row still has to
              // show what the bill was discounted by if somebody else did it.
              disabled={!can("orders:discount")}
              onClick={onDiscount}
            >
              <dt>{t("pos.cart.discount")}</dt>
              <dd>
                {cart.discountAmount > 0n
                  ? `-${formatMoney(cart.discountAmount.toString(), currency)}`
                  : t("pos.cart.addDiscount")}
              </dd>
            </button>
            {priced.serviceCharge.amount > 0n ? (
              <Row
                label={t("pos.cart.service")}
                value={formatMoney(priced.serviceCharge.amount.toString(), currency)}
              />
            ) : null}
            {priced.taxLines.map((tax) => (
              <Row
                key={tax.code}
                label={tax.name}
                value={formatMoney(tax.amount.amount.toString(), currency)}
              />
            ))}
            {/* Beside the discount rather than in the footer: both are things
                done to the bill's money, and the footer's three buttons are
                already the actions that end the service. A judgement call. */}
            {bill ? (
              <button
                type="button"
                className="flex justify-between text-left text-ink-muted underline decoration-dotted underline-offset-4"
                onClick={() => cart.setTaxExempt(!bill.isTaxExempt)}
              >
                <dt>{t("pos.cart.taxFree")}</dt>
                <dd>
                  {bill.isTaxExempt ? t("pos.cart.taxFreeRemove") : t("pos.cart.taxFreeApply")}
                </dd>
              </button>
            ) : null}
            <div className="mt-1 flex justify-between border-t border-line pt-2 text-base font-semibold text-ink-body">
              <dt>{t("pos.cart.total")}</dt>
              <dd>{formatMoney(priced.total.amount.toString(), currency)}</dd>
            </div>
          </dl>
        ) : null}

        <div className="flex gap-2">
          {/* Parking is offered on any open bill, and nothing is disabled while
              one is parked: holding is about where the party is, not about what
              the cashier is allowed to do next. A judgement call, marked. */}
          {bill ? (
            <Button
              variant="secondary"
              className="flex-1"
              onClick={() => cart.setHeld(!bill.isHeld)}
            >
              {bill.isHeld ? t("pos.cart.resume") : t("pos.cart.hold")}
            </Button>
          ) : null}
          <Button
            variant="secondary"
            className="flex-1"
            disabled={cart.unfired.length === 0}
            onClick={cart.fire}
          >
            {t("pos.cart.sendToStation")}
          </Button>
          {/* A waiter takes orders and does not take money: `WAITER` holds no
              `settlement:pay`, so the button that would be refused is not
              offered. The server refuses it regardless — this is honesty about
              what will work, not the gate. */}
          {can("settlement:pay") ? (
            <Button className="flex-1" disabled={cart.lines.length === 0} onClick={onSettle}>
              {t("pos.cart.settle")}
            </Button>
          ) : null}
        </div>
      </footer>
    </aside>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="sticky top-0 bg-surface-sunken px-3 py-1 text-xs text-ink-muted">{label}</h2>
      {children}
    </section>
  );
}

function Line({
  line,
  currency,
  cart,
  onAmend,
  isFired = false,
}: {
  line: Cart["lines"][number];
  currency: string;
  cart: Cart;
  onAmend?: (itemId: string) => void;
  isFired?: boolean;
}) {
  const options = optionsOf(line);

  return (
    <div className="flex items-start gap-2 border-b border-line px-3 py-2 last:border-0">
      {/* A note is whatever the cashier typed. Without these two it can be one
          unbroken run that refuses to wrap, and it takes the stepper and the
          price off the pane with it. */}
      <div className="min-w-0 flex-1 wrap-break-word">
        <p className="text-sm text-ink-body">{line.name}</p>
        {/* Named rather than counted, for the same reason the note below is
            printed in full: "2 options" is a cashier opening the sheet again
            to find out which two, and the station ticket says the words. */}
        {options.length > 0 ? (
          <p className="text-xs text-ink-muted">
            {options.map((option) => option.name).join(" · ")}
          </p>
        ) : null}
        {/* In full, not as a count: the point of "no ice" is the words. A
            cashier reads it back to the table, and it is the only thing on
            this line the station cannot work out from the item itself. */}
        {line.note ? <p className="text-xs text-ink-muted">{line.note}</p> : null}
      </div>

      {isFired ? (
        // With the station: changing it is a void or a return, both audited
        // and both decided by the server. The till does not quietly edit food
        // that is being cooked — it asks, and redraws from the answer.
        <button
          type="button"
          className="text-sm text-ink-muted underline decoration-dotted underline-offset-4"
          onClick={() => onAmend?.(line.key)}
        >
          × {whole(line)}
        </button>
      ) : (
        <div className="flex items-center gap-1">
          {/* The last one down takes the line with it — see `decrementLines`. */}
          <Button variant="secondary" size="sm" onClick={() => cart.decrement(line.key)}>
            −
          </Button>
          <span className="w-6 text-center text-sm text-ink-body">{whole(line)}</span>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => cart.setQuantity(line.key, whole(line) + 1)}
          >
            +
          </Button>
        </div>
      )}

      <span className="w-20 text-end text-sm text-ink-body">
        {formatMoney((unitCost(line) * BigInt(whole(line))).toString(), currency)}
      </span>
    </div>
  );
}

/**
 * A quantity as a whole number of things.
 *
 * The engine carries quantity as a scaled integer so a half-kilo of fish is
 * exact; a cart line of two coffees is `{ value: 2n, scale: 0 }`, and this is
 * the one place the till needs it as a number to put beside a plus and a minus.
 */
function whole(line: { quantity: { value: bigint; scale: number } }): number {
  return Number(line.quantity.value) / 10 ** line.quantity.scale;
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-ink-muted">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

/** The one thing a cashier must be able to see without asking anybody. */
function SyncBadge({ state }: { state: SyncState }) {
  const t = useTranslate();

  if (state.kind === "idle") return null;
  if (state.kind === "needsAttention") {
    return <Badge tone="danger">{t("pos.sync.failed", { count: state.failures })}</Badge>;
  }
  if (state.kind === "syncing") return <Badge tone="neutral">{t("pos.sync.busy")}</Badge>;
  return <Badge tone="warning">{t("pos.sync.queued", { count: state.remaining })}</Badge>;
}
